import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar, Loader2, ExternalLink, Video } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function pad(n) {
  return String(n).padStart(2, "0");
}

function toLocalInput(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const DURATIONS = [
  { label: "30 min", min: 30 },
  { label: "45 min", min: 45 },
  { label: "1 hour", min: 60 },
  { label: "1h 30m", min: 90 },
];

export default function CalendarHoldModal({
  open,
  onOpenChange,
  interviewId,
  resumeId,
  candidateName,
  candidateEmail,
  jobTitle,
  defaultRecruiterEmail,
}) {
  const [platformEnabled, setPlatformEnabled] = useState(true);
  const [connected, setConnected] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return toLocalInput(d);
  });
  const [duration, setDuration] = useState(45);
  const [extraAttendees, setExtraAttendees] = useState("");
  const [includeMeet, setIncludeMeet] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) setCreated(null);
        const { data } = await api.get("/calendar/status");
        if (cancelled) return;
        setPlatformEnabled(!!data.platform_enabled);
        setConnected(!!data.connected);
      } catch {
        // graceful — assume disconnected
      } finally {
        if (!cancelled) setStatusLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const startISO = useMemo(() => {
    if (!start) return null;
    return new Date(start).toISOString();
  }, [start]);

  const endISO = useMemo(() => {
    if (!start) return null;
    const d = new Date(start);
    d.setMinutes(d.getMinutes() + duration);
    return d.toISOString();
  }, [start, duration]);

  const submit = async () => {
    if (!startISO || !endISO) return;
    setSubmitting(true);
    try {
      const attendees = [];
      if (candidateEmail) attendees.push(candidateEmail);
      extraAttendees
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes("@"))
        .forEach((e) => attendees.push(e));
      const { data } = await api.post("/calendar/hold", {
        title: `Interview — ${candidateName || "Candidate"} · ${jobTitle || "Role"}`,
        start: startISO,
        end: endISO,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        attendee_emails: attendees,
        description: `Interview hold scheduled via Hireloop PRO${
          defaultRecruiterEmail ? ` by ${defaultRecruiterEmail}` : ""
        }.`,
        interview_id: interviewId || null,
        resume_id: resumeId || null,
        add_meet_link: includeMeet,
      });
      setCreated(data);
      toast.success("Interview booked on Google Calendar");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not book the calendar hold"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="calendar-hold-modal"
        className="max-w-md border border-slate-200"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4 text-blue-600" />
            Schedule on Google Calendar
          </DialogTitle>
        </DialogHeader>

        {statusLoading ? (
          <div className="flex h-20 items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !platformEnabled ? (
          <div
            data-testid="calendar-modal-not-configured"
            className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            Google Calendar is not configured on this server yet. Add{" "}
            <code>GOOGLE_CLIENT_ID</code> and <code>GOOGLE_CLIENT_SECRET</code>{" "}
            to the backend env to enable it.
          </div>
        ) : !connected ? (
          <div
            data-testid="calendar-modal-disconnected"
            className="space-y-3"
          >
            <div className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              You haven&apos;t connected Google Calendar yet. Head to Settings →
              Google Calendar to connect.
            </div>
            <Button
              onClick={() => (window.location.href = "/settings")}
              className="bg-blue-600 text-white hover:bg-blue-500"
              data-testid="calendar-modal-goto-settings"
            >
              Open Settings
            </Button>
          </div>
        ) : created ? (
          <div data-testid="calendar-modal-success" className="space-y-3">
            <div className="border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              Calendar hold created. Candidate has been invited.
            </div>
            <div className="space-y-2">
              <a
                href={created.html_link}
                target="_blank"
                rel="noreferrer"
                data-testid="calendar-event-link"
                className="inline-flex items-center gap-1 text-sm font-bold text-blue-700 hover:underline"
              >
                Open in Google Calendar <ExternalLink className="h-3 w-3" />
              </a>
              {created.meet_link && (
                <div>
                  <a
                    href={created.meet_link}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="calendar-meet-link"
                    className="inline-flex items-center gap-1 text-sm font-bold text-violet-700 hover:underline"
                  >
                    <Video className="h-3 w-3" /> Join Google Meet
                  </a>
                </div>
              )}
            </div>
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              data-testid="calendar-modal-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Candidate
              </label>
              <div className="text-sm text-slate-800">
                {candidateName || "—"}{" "}
                {candidateEmail && (
                  <span className="text-slate-500">
                    &lt;{candidateEmail}&gt;
                  </span>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="cal-start"
                className="text-xs font-bold uppercase tracking-wider text-slate-500"
              >
                Start
              </label>
              <input
                id="cal-start"
                data-testid="calendar-start-input"
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Duration
              </label>
              <div className="mt-1 flex gap-1">
                {DURATIONS.map((d) => (
                  <button
                    key={d.min}
                    data-testid={`calendar-duration-${d.min}`}
                    onClick={() => setDuration(d.min)}
                    className={`flex-1 border px-2 py-1.5 text-xs font-semibold ${
                      duration === d.min
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="cal-extra"
                className="text-xs font-bold uppercase tracking-wider text-slate-500"
              >
                Extra attendees (comma-separated, optional)
              </label>
              <input
                id="cal-extra"
                data-testid="calendar-extra-attendees"
                type="text"
                value={extraAttendees}
                onChange={(e) => setExtraAttendees(e.target.value)}
                placeholder="hiringmgr@company.com"
                className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                data-testid="calendar-include-meet"
                checked={includeMeet}
                onChange={(e) => setIncludeMeet(e.target.checked)}
              />
              Include a Google Meet video link
            </label>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="calendar-modal-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !start}
                data-testid="calendar-modal-submit"
                className="bg-blue-600 text-white hover:bg-blue-500"
              >
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Calendar className="mr-2 h-4 w-4" />
                )}
                Book hold
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
